/**
 * View builders — the heart of L0. Pure functions turning the metamodel
 * (`describe_schema`) + the semantic layer (`describe_semantics`) into
 * render-ready view models. Zero authoring: register an entity + its field_meta
 * and usable list / detail / dashboard screens fall out. The SAME view models
 * feed the HTML renderer here, a React host, or a connector's native chrome —
 * define once, many faces.
 */
import type {
  SchemaDescription, SemanticDescription, SemanticField, Measure, ExperienceDocument,
  EntityIdentityInfo, RelatedSectionSpec,
} from '@solum/sdk'
import type { ResponsiveColumns, ResponsiveSpanV2 } from './layout.js'
export type { ExperienceDocument, RelatedSectionSpec } from '@solum/sdk'

/**
 * One option of a picklist, as `core.catalog_options` returns it (#1075).
 *
 * `selectable: false` means the value is present, labelled and rendered, but
 * closed to a NEW choice — a vocabulary entry the deployment has archived. The
 * row is deliberately NOT dropped: this same array resolves the LABEL of a
 * value already stored on a record, so dropping it makes a historical value
 * render as a blank placeholder — absent rather than archived.
 *
 * Absent reads as SELECTABLE: a catalogue that declares no availability rule,
 * and an older core that does not answer the question, must behave as before.
 */
export interface FieldOption {
  value: string
  label: string
  tone?: 'good' | 'warn' | 'critical' | 'neutral'
  selectable?: boolean
}
/**
 * `core.field_meta.rules`, as the renderer reads it.
 *
 * `multiline` was MISSING here while 25 field registrations across five
 * modules and core already wrote `"multiline": true` into the column —
 * compliance policy bodies, ISMS scope statements, corrective actions,
 * internal-audit findings, tender answers. The database has been describing
 * those fields correctly since they shipped; this type dropped the word, so
 * nothing downstream could read it and every one of them rendered as a
 * one-line `<input>`.
 */
export interface FieldRules { min?: number; max?: number; minLength?: number; maxLength?: number; pattern?: string; multiline?: boolean }
export interface Column {
  field: string; label: string; kind: string
  format?: string | null; classification: string; options: FieldOption[]
  required: boolean; readOnly: boolean; section: string | null; rules: FieldRules
  /**
   * The server fills this column when a statement omits it (#1579). Carried
   * from `SemanticField.has_server_default`, which core derives from
   * `pg_attribute` rather than storing a second time.
   *
   * It changes CREATE only. `required` still means the column is NOT NULL, and
   * on an existing record it still means what it always meant — but on a NEW
   * record a required-and-defaulted column is not a blank the user must fill,
   * and an untouched one is omitted from the patch so the database supplies it.
   */
  hasServerDefault: boolean
  /**
   * Presentation-only width from a governed LayoutDocument, per profile. It
   * cannot alter the field's type, classification, authority or validation.
   *
   * RESPONSIVE since #2009, and already CLAMPED to its profile's track count by
   * `applyFormLayout` — a host renders the number it is given and never
   * re-decides geometry. A v1 document reaches here through the same
   * normalization, so there is one shape to render rather than two.
   */
  layoutSpan?: ResponsiveSpanV2
}
export interface ListView { entity: string; title: string; columns: Column[] }
export interface DetailView { entity: string; title: string; fields: Column[] }
/**
 * One section of a form.
 *
 * `related` carries the definition's related LIST through to the host
 * (core `2026080301`). It used to be dropped here: `buildForm` read `title` and
 * `fields` and nothing else, so a definition could declare a related section
 * that no surface could ever draw — and, because the grammar makes `fields`
 * optional for exactly that section, the drop was not even silent. The shape
 * the migration header documents verbatim,
 * `{"title": "People", "related": {"rel_type": "works_at", "direction": "in"}}`,
 * threw `TypeError: Cannot read properties of undefined (reading 'map')` at
 * `views.ts:105` and took the whole record surface with it. Measured before the
 * fix, on that literal document.
 *
 * The relationship is NOT resolved here — `packages/experience` has no schema
 * door and no notion of a slot. The host matches `rel_type` + `direction`
 * against the slots it already derives from `core.relationship_type` (#484), so
 * there is one derivation of "what can be related to this record" and the form
 * document only says WHERE and under WHAT TITLE one of them is placed.
 */
export interface FormSection {
  title: string
  fields: Column[]
  /** Stable layout identity and desktop column count, when an authored
   * LayoutDocument supplied them. Older form documents remain valid. */
  layoutId?: string
  /**
   * The section's column track per profile (#2009). A v1 document's scalar is
   * normalized on the way in — `{ desktop: N, tablet: min(N,2), phone: 1 }` —
   * so a reader never sees the old shape and never rewrites the stored row.
   */
  layoutColumns?: ResponsiveColumns
  /** the definition's prose under the title, when it gave one */
  description?: string
  /** a related list this section places, or absent for an ordinary field section */
  related?: RelatedSectionSpec
}
export interface FormView { entity: string; title: string; sections: FormSection[] }
export interface DashboardCard { key: string; label: string; aggregation: string; format?: string | null }
export interface NavGroup { module: string; entities: { key: string; label: string; iconKey?: string }[] }

export interface EntityIdentity {
  key: string
  module: string
  singularLabel: string
  pluralLabel: string
  iconKey: string
  titleDeclared: boolean
}

/** One safe client shape. Unknown keys never become display copy. */
export function resolveEntityIdentity(
  schema: SchemaDescription,
  identities: readonly EntityIdentityInfo[] | undefined,
  entity: string,
): EntityIdentity {
  const structural = schema.entities.find((item) => item.key === entity)
  const resolved = identities?.find((item) => item.entity_type === entity)
  const safeLabel = structural?.label?.trim() || 'Record'
  return {
    key: entity,
    module: resolved?.module ?? structural?.module ?? '',
    singularLabel: resolved?.singular_label?.trim() || safeLabel,
    pluralLabel: resolved?.plural_label?.trim() || safeLabel,
    iconKey: resolved?.icon_key?.trim() || 'record',
    titleDeclared: Boolean(resolved?.title_declared),
  }
}

const toColumn = (f: SemanticField): Column => ({
  field: f.column_name, label: f.label, kind: f.kind, format: f.format,
  classification: f.classification, options: (f.options as FieldOption[]) ?? [],
  required: Boolean(f.required), readOnly: Boolean(f.read_only),
  section: f.section ?? null, rules: (f.rules as FieldRules) ?? {},
  // Absent reads as FALSE, deliberately: an older core that does not answer
  // this question must not have its silence read as "the server will fill it".
  hasServerDefault: Boolean(f.has_server_default),
})

/**
 * The same shape `core.lint_experience` rule 6 accepts, checked here too.
 *
 * Not distrust of the lint — the lint is a REPORT, not a constraint, and it does
 * not even cover the document this builder is usually handed. Rule 6 reads
 * `core.form` (`2026080301:114`); the console's `buildForm` is fed
 * `core.get_experience`, i.e. `core.experience.document`, which shares the
 * sections grammar (`0021:20`) and is checked only for unknown FIELDS
 * (`2026080301:92-99`). A related section written there is unlinted today.
 *
 * So the legacy vocabulary the migration retired (`"related": "contacts"`, a
 * bare string naming nothing any table ever held) can still sit in either store.
 * Carrying one through would put a titled, permanently empty section on a record
 * — a bad row rendered as a hole in the UI. Refusing it here leaves the surface
 * as it was, and the definition as the place the row gets fixed.
 *
 * A VALID declaration being dropped is the defect this slice fixes; an INVALID
 * one being dropped is this function agreeing with the database.
 */
export function relatedSpec(value: unknown): RelatedSectionSpec | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const { rel_type: relType, direction } = value as Record<string, unknown>
  if (typeof relType !== 'string' || !relType) return null
  if (direction !== 'in' && direction !== 'out') return null
  return { rel_type: relType, direction }
}

const fieldsFor = (semantics: SemanticDescription, entity: string): SemanticField[] =>
  semantics.fields.filter((f) => f.entity_type === entity).sort((a, b) => a.sort - b.sort)

function entityLabel(schema: SchemaDescription, entity: string): string {
  return schema.entities.find((e) => e.key === entity)?.label ?? entity
}

/** Naive English number helpers for entity nouns. Entity labels in the seed are
 *  plural collection names; forms want the singular. Regular cases only —
 *  irregulars (People/Person) are left to the label author. */
export function pluralize(s: string): string {
  return /y$/i.test(s) ? s.slice(0, -1) + 'ies' : s ? s + 's' : s
}
export function singularize(s: string): string {
  return /ies$/i.test(s) ? s.slice(0, -3) + 'y' : /[^s]s$/i.test(s) ? s.slice(0, -1) : s
}

/** Columns for a list. L1 doc `columns` (explicit order/subset) wins; else the
 *  L0 default = the `is_summary` fields (fallback: first 4 known fields). */
export function buildListView(schema: SchemaDescription, semantics: SemanticDescription, entity: string, doc?: ExperienceDocument): ListView {
  const fields = fieldsFor(semantics, entity)
  const title = entityLabel(schema, entity)   // the seed label is the collection name
  if (doc?.columns?.length) {
    const byKey = new Map(fields.map((f) => [f.column_name, f]))
    const chosen = doc.columns.map((k) => byKey.get(k)).filter((f): f is SemanticField => Boolean(f))
    return { entity, title, columns: chosen.map(toColumn) }
  }
  const summary = fields.filter((f) => f.is_summary)
  const chosen = summary.length ? summary : fields.slice(0, 4)
  return { entity, title, columns: chosen.map(toColumn) }
}

/** A sectioned form. L1 doc `sections` (explicit titles/order/subset) wins; else
 *  the L0 default groups by each field's `section`. */
export function buildForm(schema: SchemaDescription, semantics: SemanticDescription, entity: string, doc?: ExperienceDocument): FormView {
  const all = fieldsFor(semantics, entity).map(toColumn)
  const title = entityLabel(schema, entity)
  if (doc?.sections?.length) {
    const byKey = new Map(all.map((c) => [c.field, c]))
    const sections = doc.sections.map((sec): FormSection => {
      const related = relatedSpec(sec.related)
      return {
        title: sec.title,
        // `fields` is optional in the grammar (`core/0030:93`) and absent on
        // every related-only section. `?? []` is the whole difference between a
        // section and a thrown TypeError — see FormSection above.
        fields: (sec.fields ?? []).map((k) => byKey.get(k)).filter((c): c is Column => Boolean(c)),
        ...(sec.description ? { description: sec.description } : {}),
        ...(related ? { related } : {}),
      }
    })
    return { entity, title, sections }
  }
  return { entity, title, sections: groupSections(all) }
}

/** Detail = every dictionaried field, in sort order. */
export function buildDetailView(schema: SchemaDescription, semantics: SemanticDescription, entity: string): DetailView {
  return { entity, title: entityLabel(schema, entity), fields: fieldsFor(semantics, entity).map(toColumn) }
}

/** Group detail fields into their sections (preserving first-seen order) — zero-authoring form layout. */
export function groupSections(fields: Column[]): FormSection[] {
  const order: string[] = []
  const bySection = new Map<string, Column[]>()
  for (const f of fields) {
    const sec = f.section ?? 'Details'
    if (!bySection.has(sec)) { bySection.set(sec, []); order.push(sec) }
    bySection.get(sec)!.push(f)
  }
  return order.map((title) => ({ title, fields: bySection.get(title)! }))
}

/** Dashboard cards = the registered measures (optionally scoped to one entity). */
export function buildDashboard(semantics: SemanticDescription, entity?: string): DashboardCard[] {
  return semantics.measures
    .filter((m: Measure) => !entity || m.entity_type === entity)
    .map((m) => ({ key: m.key, label: m.label, aggregation: m.aggregation, format: m.format }))
}

/** Navigation = registered entities grouped by module (only those with a dictionary). */
export function buildNav(
  schema: SchemaDescription,
  semantics: SemanticDescription,
  identities?: readonly EntityIdentityInfo[],
): NavGroup[] {
  const dictionaried = new Set(semantics.fields.map((f) => f.entity_type))
  const byModule = new Map<string, { key: string; label: string; iconKey?: string }[]>()
  for (const e of schema.entities) {
    if (!dictionaried.has(e.key)) continue
    const list = byModule.get(e.module) ?? []
    const identity = resolveEntityIdentity(schema, identities, e.key)
    list.push({ key: e.key, label: identity.pluralLabel, iconKey: identity.iconKey })
    byModule.set(e.module, list)
  }
  return [...byModule.entries()].map(([module, entities]) => ({ module, entities }))
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE USER RUNG — a person's own order for a form's elements (#1466 M4)
 *
 * What a person may move is exactly what the finite grammar already has: the
 * SECTIONS of a form, and the FIELDS within a section. Everything that decides
 * what a field MEANS is deliberately OUT OF REACH here and is not merely
 * unimplemented — `required`, `readOnly`, `classification`, `core.field_acl`
 * and the entity's write door are governed elsewhere, and a personal preference
 * that could touch one of them would be a privilege escalation dressed as a
 * layout. `applyFormOrder` is a PERMUTATION: the output carries every section
 * the definition built, every field inside the section the definition put it
 * in, and nothing else. It cannot add, drop, retype or unhide anything, and the
 * tests assert that as a property rather than as an example.
 *
 * ── READ-ONLY as shipped, and the way that nearly went wrong ────────────────
 * `applyFormOrder` is the only half of the rung the product REACHES. There is
 * no `formOrderOf` any more and no editor anywhere: a reorder GESTURE cannot be
 * verified in either package's suite (the console has no DOM — see
 * `useFormOrder.ts`, which keeps the writing half and says why). So on every
 * record in the console today this function is handed `null` and returns the
 * built view unchanged. That is the correct inert state, not a stub: the reader
 * is the half that must already be safe when a document first exists, because
 * the document is jsonb its owner writes.
 *
 * It is handed `null` **since this slice**. Before it, the console passed the
 * `useFormOrder` handle — the object holding the order, not the order — and
 * because every property of `FormOrder` is optional, that typechecked. The
 * guard read the handle as an empty order and returned the view unchanged, so
 * the observable behaviour was identical to the intended inert state and
 * nothing could tell the two apart. The moment anything wrote an order, the
 * reader would have gone on ignoring it.
 *
 * The parameter is now an EXACT type, so the mistake does not compile. An
 * all-optional interface is a type that cannot say no, and reaching for one to
 * describe a document whose fields are genuinely all optional is how you get a
 * parameter that accepts the entire program.
 *
 * ── Where the order LIVES, and the RAISE that decided it ────────────────────
 * `core.user_setting`, not a `scope='user'` row in `core.form`.
 *
 * `core.form` carries a partial unique index for a system default and for a
 * persona default (`core/0030:101-102`) and NONE for `scope='user' and
 * is_default`. `core.resolve_form` is `language sql` and reads that layer as a
 * scalar subquery inside `coalesce(...)`, so a second personal default does not
 * lose a tie-break — it makes the resolver RAISE `more than one row returned by
 * a subquery used as an expression`, and the form stops resolving for that user
 * on that entity. Putting a reorder in that table means one duplicate row away
 * from a broken record surface, for a preference about where a box sits.
 *
 * `core.user_setting` is keyed `(user_id, key)` — a primary key, so "two of
 * them" is not a state that exists — needs no migration, and keeps the ALM
 * boundary for free: a solution exports `core.form`, and a person's own layout
 * is not something a solution should ever carry between environments.
 *
 * ── Reconciliation is by TITLE, and titles are not ids ──────────────────────
 * The grammar gives a section no identity but its title (`core/0030:93`), so a
 * pin can only name one. Rename a section in the definition and that section's
 * pin stops matching — it falls back to the definition's order, which is the
 * correct direction to fail: the definition wins and nothing disappears.
 * Duplicate titles are possible (nothing forbids them), so a pinned title
 * consumes the first section not already taken; that is what keeps the result a
 * permutation instead of duplicating one section and dropping another.
 *
 * An element the pin does not name — a field a module SHIPPED after the person
 * saved their order — is appended in definition order, never omitted. A
 * personalisation that could hide a new required field is how a form silently
 * stops being fillable.
 * ──────────────────────────────────────────────────────────────────────────── */

/** One person's layout for one entity's form. Both halves are optional: a pin
 *  may reorder sections without touching fields, or one section's fields
 *  without touching the section order. */
/**
 * One person's order for one entity's form: section titles, and the field keys
 * within a section. Both halves are optional INDIVIDUALLY — an order may say
 * only "these sections, in this order" and leave the fields alone — but an
 * order that says NEITHER is not an empty order, it is not an order.
 *
 * Hence the union rather than the obvious interface with two optional
 * properties. That interface is a type that cannot say no: every property being
 * optional makes every object in the program assignable to it, and
 * `applyFormOrder` was duly handed the `useFormOrder` HANDLE instead of the
 * handle's `.order`. It typechecked, the guard read the handle as an order
 * carrying nothing, and the whole rung was inert while the compiler was green
 * and the header above said the inertness was on purpose.
 *
 * Written this way, an object with neither key — the handle among them — is
 * assignable to neither arm and does not compile. The state the runtime guard
 * already treated as "no order" is now also the state the type will not build.
 */
export type FormOrder =
  | { sections: string[]; fields?: Record<string, string[]> }
  | { sections?: undefined; fields: Record<string, string[]> }

/** Pinned-first, then the rest in definition order; each item consumed once. */
function reorderBy<T>(items: T[], keyOf: (item: T) => string, pinned: string[] | undefined): T[] {
  if (!pinned?.length) return items
  const remaining = [...items]
  const out: T[] = []
  for (const key of pinned) {
    const at = remaining.findIndex((item) => keyOf(item) === key)
    if (at >= 0) out.push(...remaining.splice(at, 1))
  }
  return [...out, ...remaining]
}

/**
 * Apply a person's order to a built form. Returns the SAME object when there is
 * nothing to apply, so a surface with no pin pays nothing and re-renders no
 * more than it did before this existed.
 */
export function applyFormOrder(view: FormView, order?: FormOrder | null): FormView {
  if (!order || (!order.sections?.length && !Object.keys(order.fields ?? {}).length)) return view
  const sections = reorderBy(view.sections, (sec) => sec.title, order.sections)
    .map((sec) => {
      const pinned = order.fields?.[sec.title]
      if (!pinned?.length) return sec
      return { ...sec, fields: reorderBy(sec.fields, (f) => f.field, pinned) }
    })
  return { ...view, sections }
}
